/**
 * index.tsx (CardList) is a dependency of the case studies display which is out of scope.
 * Duplicated index.tsx (CardList) to support new design requirements on vertical area LPs
 */
import React, { Fragment, forwardRef, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import IconDown from 'images/icons/Icon_Down.svg';
import QueryParamLink from 'components/atoms/QueryParamLink';
import styles from './CardListV2.module.scss';
import Pagination from 'components/molecules/Pagination';
import CardLoading from 'components/molecules/CardLoading';

interface Props {
  cardRenderer: (dataItem: Record<string, any> & { classes?: string }) => React.ReactNode;
  currentPage: number;
  data: Array<Record<string, any>>;
  isLoadMoreEnabled?: boolean;
  loadingPlaceholderHeight?: string;
  pageQueryParamName: string;
  pageSize: number;
  testId?: string;
  totalItems: number;
}

const SeeMoreAnchorIconButton = React.forwardRef<
  HTMLAnchorElement,
  {
    href?: string;
    onClick?: () => void;
    testId: string;
    text: string;
  }
>(({ onClick, href, text, testId }, ref) => (
  <a
    onClick={onClick}
    ref={ref}
    href={href}
    className={styles['load-more-mobile']}
    data-test-id={`${testId}-load-more`}
  >
    <span className={styles['see-more-pagination']}>
      {text}
      <IconDown height={24} width={24} />
    </span>
  </a>
));
SeeMoreAnchorIconButton.displayName = 'SeeMoreAnchorIconButton';

const CardListV2 = forwardRef<HTMLDivElement, Props>(
  (
    {
      currentPage,
      data,
      isLoadMoreEnabled = true,
      pageQueryParamName = '',
      pageSize,
      testId = '',
      totalItems = 0,
      cardRenderer,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const canLoadMore = isLoadMoreEnabled && pageSize * currentPage < totalItems;
    const itemsSortedByPage = useMemo(() => _.sortBy(data, 'page'), [data]);

    return (
      <div ref={ref} className={styles.container} data-test-id={testId}>
        <label className={styles['pro-count-header']}>
          {t('marketingPages:totalPros', { totalPros: totalItems.toLocaleString() })}
        </label>
        <div className={classNames(styles.cards)}>
          {itemsSortedByPage.length
            ? itemsSortedByPage
                .map((dataItem) => (
                  <Fragment key={dataItem.id as string}>
                    {cardRenderer({
                      ...dataItem,
                      classes: classNames(styles['card-container'], {
                        [styles['card-container-hidden']]: dataItem.page !== currentPage,
                      }),
                    })}
                  </Fragment>
                ))
                .concat(
                  Array.from(
                    {
                      length: itemsSortedByPage.some((dataItem) => dataItem.page === currentPage)
                        ? 0
                        : pageSize,
                    },
                    (_value, index) => (
                      <article className={styles['card-container']} key={`${currentPage}-${index}`}>
                        <CardLoading testId={`${testId}-card`} />
                      </article>
                    )
                  )
                )
            : canLoadMore &&
              Array.from({ length: pageSize }, (_value, i) => (
                <article
                  className={styles['card-container']}
                  key={`${testId}-card-loading${i + 1}`}
                >
                  <CardLoading testId={`${testId}-card`} />
                </article>
              ))}
          {canLoadMore && (
            <QueryParamLink
              params={{ [pageQueryParamName]: (currentPage + 1).toString() }}
              passHref={true}
            >
              <SeeMoreAnchorIconButton
                text={t('seeMore')}
                testId={testId}
              ></SeeMoreAnchorIconButton>
            </QueryParamLink>
          )}
        </div>
        {isLoadMoreEnabled && pageSize < totalItems && (
          <nav className={styles.pagination}>
            <Pagination
              currentPage={currentPage}
              itemsPerPage={pageSize}
              totalItems={totalItems}
              pageQueryParamName={pageQueryParamName}
              testId={testId}
            />
          </nav>
        )}
      </div>
    );
  }
);
CardListV2.displayName = 'CardListV2';

export type { Props as CardListProps };
export default CardListV2;
